// Admin subscriptions log — read-only listing of every subscription
// across communities. Filters by status + community + search.
//
// Cancellations and refunds happen on the subscription-payments page
// or via the products page (force-cancel-all). This page is for
// observability and support triage — when a subscriber emails about
// their sub, the operator finds it here.
import { useCallback, useEffect, useState } from "react";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Select } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

interface SubRow {
  id: string;
  subscriber_id: string;
  community_id: string;
  billing_interval: string;
  price_usd_at_subscribe: number;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  created_at: string;
  communities: { id: string; slug: string; name: string } | null;
  profiles: { id: string; username: string } | null;
}

interface ListResponse {
  subscriptions: SubRow[];
  total: number;
}

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "past_due", label: "Past due" },
  { value: "trialing", label: "Trialing" },
  { value: "canceled", label: "Canceled" },
  { value: "incomplete", label: "Incomplete" },
  { value: "incomplete_expired", label: "Incomplete expired" },
  { value: "unpaid", label: "Unpaid" },
  { value: "paused", label: "Paused" },
];

function formatUsd(n: number): string {
  return `$${Number(n).toFixed(2)}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusColor(s: string): string {
  switch (s) {
    case "active":
    case "trialing":
      return "var(--upvote, #22C55E)";
    case "past_due":
    case "unpaid":
      return "var(--danger, #ef4444)";
    case "canceled":
    case "incomplete_expired":
      return "var(--text-secondary)";
    case "incomplete":
    case "paused":
      return "var(--accent)";
    default:
      return "var(--text-primary)";
  }
}

export default function AdminSubscriptionsPage() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (debouncedSearch) params.set("search", debouncedSearch);
      params.set("limit", "100");
      const res = await api<ListResponse>(
        `/admin/subscriptions?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const rows = data?.subscriptions ?? [];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.h1}>Subscriptions</h1>
        <CardSubtle>
          Every community subscription on the platform. Filter to triage
          support issues; refunds and force-cancels happen on the
          payments and products pages.
        </CardSubtle>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={styles.filterRow}>
          <div style={{ flex: 1 }}>
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <div style={{ flex: 2 }}>
            <Label>Search</Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="username or community slug"
            />
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div style={styles.loadingWrap}>
            <Spinner />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <div style={styles.errorBar}>{error}</div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardTitle>No subscriptions</CardTitle>
          <CardSubtle>Nothing matches the current filters.</CardSubtle>
        </Card>
      ) : (
        <Card padding={0}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHead}>
                <Th>Subscriber</Th>
                <Th>Community</Th>
                <Th>Plan</Th>
                <Th>Status</Th>
                <Th>Next billing</Th>
                <Th>Created</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={styles.tableRow}>
                  <Td>@{row.profiles?.username ?? "(deleted)"}</Td>
                  <Td>r/{row.communities?.slug ?? "?"}</Td>
                  <Td>
                    {formatUsd(row.price_usd_at_subscribe)} /{" "}
                    {row.billing_interval === "monthly" ? "month" : "year"}
                  </Td>
                  <Td>
                    <span style={{ color: statusColor(row.status) }}>
                      {row.cancel_at_period_end && row.status !== "canceled"
                        ? "canceling"
                        : row.status}
                    </span>
                  </Td>
                  <Td>{formatDate(row.current_period_end)}</Td>
                  <Td>
                    <CardSubtle style={{ fontSize: 12 }}>
                      {formatDate(row.created_at)}
                    </CardSubtle>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th style={styles.th}>{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td style={styles.td}>{children}</td>;
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 16 },
  h1: { margin: 0, fontSize: 24, fontWeight: 700, color: "var(--text-primary)" },
  filterRow: { display: "flex", gap: 12 },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 0",
  },
  errorBar: {
    background: "rgba(239,68,68,0.1)",
    color: "var(--danger, #ef4444)",
    padding: 12,
    borderRadius: "var(--radius-sm)",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  tableHead: { background: "rgba(255,255,255,0.02)", textAlign: "left" },
  tableRow: { borderTop: "1px solid var(--border)" },
  th: {
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-secondary)",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  td: { padding: 12, verticalAlign: "top", color: "var(--text-primary)" },
};
